import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as playApi from '../api/playApi';
import { usePolling } from '../hooks/usePolling';
import { useThemeColors } from '../hooks/useThemeColors';
import SemicircleBoxes from '../components/game/SemicircleBoxes';
import { PrizeBoardColumn } from '../components/game/PrizeBoardSplit';
import OfferDisplay from '../components/game/OfferDisplay';
import GameHeader from '../components/game/GameHeader';
import RevealAnimation from '../components/game/RevealAnimation';
import StudioBackground from '../components/game/StudioBackground';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// Premi di gioco
const DEFAULT_PRIZES = [
  0, 1, 5, 10, 20, 50, 75, 100, 200, 500,
  10000, 15000, 20000, 30000, 50000, 75000, 100000, 200000, 300000,
  { value: 0, label: 'SCATOLA MISTERIOSA' },
];

const layoutCSS = `
  .pg-tv-layout {
    display: grid;
    grid-template-columns: 170px 1fr 170px;
    flex: 1;
    gap: 0;
    position: relative;
    z-index: 1;
  }
  .pg-board-left, .pg-board-right {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 16px 8px;
    z-index: 2;
  }
  .pg-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 4px;
    gap: 16px;
    z-index: 1;
  }
  @media (max-width: 900px) {
    .pg-tv-layout {
      grid-template-columns: 1fr !important;
      grid-template-rows: auto 1fr !important;
    }
    .pg-boards-mobile {
      display: flex !important;
      gap: 6px;
      padding: 8px;
      justify-content: center;
    }
    .pg-board-left, .pg-board-right {
      display: none !important;
    }
    .pg-center {
      padding: 4px !important;
      gap: 8px !important;
    }
  }
  @media (min-width: 901px) {
    .pg-boards-mobile { display: none !important; }
  }
`;

const waitingStyles = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-xl)',
    minHeight: '100vh',
    textAlign: 'center',
    padding: 'var(--space-xl)',
    backgroundColor: 'var(--color-bg-primary)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '4rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    margin: 0,
    textShadow: '0 0 60px rgba(212, 175, 55, 0.4)',
    lineHeight: 1.1,
  },
  divider: {
    width: '120px',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, var(--color-primary), transparent)',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: 'var(--color-text-muted)',
    margin: 0,
    maxWidth: '400px',
    fontFamily: 'var(--font-body)',
  },
  error: {
    fontSize: '1rem',
    color: 'var(--color-danger)',
    margin: 0,
    fontFamily: 'var(--font-body)',
  },
};

export default function PublicGame() {
  const { code } = useParams();
  const [game, setGame] = useState(null);
  const [error, setError] = useState(null);
  const [reveal, setReveal] = useState(null);
  const prevBoxesRef = useRef(null);

  useThemeColors(game?.theme);

  const lastJsonRef = useRef(null);

  const pollGameState = useCallback(async () => {
    if (!code) return;
    try {
      const data = await playApi.getGame(code);
      const json = JSON.stringify(data);
      if (json !== lastJsonRef.current) {
        lastJsonRef.current = json;
        setGame(data);
      }
      setError(null);
    } catch {
      // silently ignore polling errors
    }
  }, [code]);

  useEffect(() => {
    if (!code) {
      setError('Nessun codice partita specificato.');
      return;
    }
    const load = async () => {
      try {
        const data = await playApi.getGame(code);
        setGame(data);
      } catch {
        setError('Partita non trovata.');
      }
    };
    load();
  }, [code]);

  usePolling(pollGameState, 2000, !!code && !error);

  // Detect newly opened boxes for reveal animation
  useEffect(() => {
    if (!game?.boxes) return;
    if (prevBoxesRef.current) {
      const prevBoxes = prevBoxesRef.current;
      for (const box of game.boxes) {
        if (box.is_opened && (box.prize_value !== undefined || box.prize_label)) {
          const prevBox = prevBoxes.find((b) => b.box_number === box.box_number);
          if (prevBox && !prevBox.is_opened) {
            setReveal({ boxNumber: box.box_number, prizeValue: box.prize_value, prizeLabel: box.prize_label });
            break;
          }
        }
      }
    }
    prevBoxesRef.current = game.boxes;
  }, [game?.boxes]);

  if (error || !code) {
    return (
      <div style={waitingStyles.container}>
        <StudioBackground intensity="high" />
        <motion.h1 style={waitingStyles.title} initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          PICKBOXGAME
        </motion.h1>
        <motion.div style={waitingStyles.divider} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.4 }} />
        <motion.p style={waitingStyles.error} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          {error || 'Link non valido.'}
        </motion.p>
      </div>
    );
  }

  if (!game) {
    return (
      <div style={waitingStyles.container}>
        <StudioBackground intensity="high" />
        <motion.h1 style={waitingStyles.title} initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          PICKBOXGAME
        </motion.h1>
        <motion.div style={waitingStyles.divider} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.4 }} />
        <motion.p style={waitingStyles.subtitle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          Caricamento partita...
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          <LoadingSpinner size="lg" />
        </motion.div>
      </div>
    );
  }

  const allPrizes = game.all_prizes && game.all_prizes.length > 0 ? game.all_prizes : DEFAULT_PRIZES;
  const hasOffer = game.last_offer && game.last_offer.status === 'pending';
  const isCompleted = game.status === 'completed';
  const showFinalReveal = isCompleted && game.final_prize_value !== undefined && game.final_prize_value !== null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--color-bg-primary)', position: 'relative' }}>
      <style>{layoutCSS}</style>
      <StudioBackground intensity="high" />
      <GameHeader game={game} />

      {/* Mobile boards row */}
      <div className="pg-boards-mobile">
        <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="low" />
        <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="high" />
      </div>

      {/* TV Show layout */}
      <div className="pg-tv-layout">
        <div className="pg-board-left">
          <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="low" />
        </div>

        <div className="pg-center">
          {hasOffer && <OfferDisplay lastOffer={game.last_offer} />}

          <SemicircleBoxes
            boxes={game.boxes || []}
            playerBoxNumber={game.player_box_number}
            disabled
          />

          {showFinalReveal && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                textAlign: 'center',
                padding: 'var(--space-xl)',
                backgroundColor: 'rgba(212, 175, 55, 0.08)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid rgba(212, 175, 55, 0.2)',
              }}
            >
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: '0.9rem',
                color: 'var(--color-text-muted)', letterSpacing: '0.1em',
                textTransform: 'uppercase', marginBottom: 'var(--space-sm)',
              }}>
                Premio Finale
              </div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 700,
                color: 'var(--color-primary)', textShadow: '0 0 30px rgba(212,175,55,0.4)',
              }}>
                {(() => {
                  const playerBox = game.boxes?.find(b => b.is_player_box);
                  if (playerBox?.prize_label && game.final_prize_value === 0) return playerBox.prize_label;
                  return game.final_prize_value.toLocaleString('it-IT', {
                    style: 'currency', currency: 'EUR',
                    minimumFractionDigits: game.final_prize_value < 1 ? 2 : 0, maximumFractionDigits: 2,
                  });
                })()}
              </div>
              {game.offer_accepted !== null && game.offer_accepted !== undefined && (
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: '0.85rem',
                  color: game.offer_accepted ? 'var(--color-success)' : 'var(--color-info)',
                  letterSpacing: '0.05em', marginTop: 'var(--space-sm)',
                }}>
                  {game.offer_accepted ? 'Offerta accettata' : 'Scatola aperta'}
                </div>
              )}
            </motion.div>
          )}
        </div>

        <div className="pg-board-right">
          <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="high" />
        </div>
      </div>

      <RevealAnimation
        isVisible={!!reveal}
        boxNumber={reveal?.boxNumber}
        prizeValue={reveal?.prizeValue}
        prizeLabel={reveal?.prizeLabel}
        onComplete={() => setReveal(null)}
        autoClose
      />
    </div>
  );
}
